import type { ExecutionPriority, ExecutionTask, GateTagType, IntakeItem, IntakePriority, IntakeSource, IntakeStatus, IntakeType } from "@/engine/types";
import { logActivity, makeExecutionTask } from "./execution-task-ops";
import { addGateTag, makeGateTag } from "./gate-ops";
import { newId } from "./tree-ops";

/**
 * 요구사항(Intake) 대기열. 심사 전에 WBS를 건드리지 않고,
 * 승인하면 새 실행 작업으로 만들거나 기존 작업에 게이트를 붙여 병합한다.
 */

export const INTAKE_SOURCES: IntakeSource[] = ["internal", "client", "voc", "bug", "regulation"];
export const INTAKE_TYPES: IntakeType[] = ["bug", "specChange", "newScope", "techDebt"];
export const INTAKE_STATUSES: IntakeStatus[] = ["pending", "reviewing", "accepted", "rejected", "deferred"];
export const INTAKE_PRIORITIES: IntakePriority[] = ["urgent", "high", "medium", "low"];

export const INTAKE_SOURCE_LABEL: Record<IntakeSource, string> = {
  internal: "내부",
  client: "고객",
  voc: "고객 의견",
  bug: "결함",
  regulation: "법규",
};

export const INTAKE_TYPE_LABEL: Record<IntakeType, string> = {
  bug: "결함 수정",
  specChange: "스펙 변경",
  newScope: "신규 범위",
  techDebt: "기술 부채",
};

export const INTAKE_STATUS_LABEL: Record<IntakeStatus, string> = {
  pending: "미심사",
  reviewing: "심사중",
  accepted: "승인",
  rejected: "반려",
  deferred: "보류",
};

export const INTAKE_PRIORITY_LABEL: Record<IntakePriority, string> = {
  urgent: "긴급",
  high: "높음",
  medium: "보통",
  low: "낮음",
};

/** 고객·고객 의견·법규는 밖에서 온 요청이라 승인 시 고객 인수 검수를 하나 더 붙인다. */
const EXTERNAL_SOURCES = new Set<IntakeSource>(["client", "voc", "regulation"]);

const TASK_PRIORITY: Record<IntakePriority, ExecutionPriority> = {
  urgent: "urgent",
  high: "important",
  medium: "medium",
  low: "low",
};

export function intakeCode(seq: number): string {
  return `INTAKE-${seq}`;
}

export function isOpenIntake(item: Pick<IntakeItem, "status">): boolean {
  return item.status === "pending" || item.status === "reviewing";
}

/** 승인으로 연결된 실행 작업이 있으면 진행·완료를 돌려준다. 없으면 null. */
export function intakeProgress(item: IntakeItem, tasks: readonly ExecutionTask[]): { done: boolean } | null {
  if (item.status !== "accepted") return null;
  const id = item.createdExecutionTaskId ?? item.targetExecutionTaskId;
  if (!id) return null;
  const task = tasks.find((t) => t.id === id);
  if (!task) return null;
  return { done: task.status === "done" };
}

export interface AcceptOptions {
  by: string;
  linkedTaskIds?: string[];
  bucketId?: string;
  note?: string;
  estimatedEffortMM?: number;
  scheduleImpactDays?: number;
}

export interface IntakeBoard {
  intakes: IntakeItem[];
  executionTasks: ExecutionTask[];
}

export function createIntakeItem(list: IntakeItem[], partial: Partial<IntakeItem> = {}, now = new Date().toISOString()): { list: IntakeItem[]; item: IntakeItem } {
  const seq = list.reduce((max, item) => Math.max(max, item.seq), 0) + 1;
  const item: IntakeItem = {
    id: newId("in"),
    seq,
    title: partial.title?.trim() ?? "",
    description: partial.description?.trim() ?? "",
    source: isSource(partial.source) ? partial.source : "internal",
    type: isType(partial.type) ? partial.type : "newScope",
    status: "pending",
    priority: isPriority(partial.priority) ? partial.priority : "medium",
    author: partial.author?.trim() ?? "",
    acceptanceCriteria: cleanLines(partial.acceptanceCriteria),
    attachments: Array.isArray(partial.attachments) ? partial.attachments : [],
    createdAt: now,
    updatedAt: now,
  };
  if (partial.organization?.trim()) item.organization = partial.organization.trim();
  if (typeof partial.estimatedEffortMM === "number" && partial.estimatedEffortMM >= 0) item.estimatedEffortMM = partial.estimatedEffortMM;
  if (typeof partial.scheduleImpactDays === "number" && partial.scheduleImpactDays >= 0) item.scheduleImpactDays = partial.scheduleImpactDays;
  if (partial.targetScheduleTaskId) item.targetScheduleTaskId = partial.targetScheduleTaskId;
  if (isGateType(partial.gateTagType)) item.gateTagType = partial.gateTagType;
  return { list: [...list, item], item };
}

export function updateIntakeItem(list: IntakeItem[], id: string, patch: Partial<IntakeItem>): IntakeItem[] {
  if (!list.some((item) => item.id === id)) return list;
  const now = new Date().toISOString();
  return list.map((item) => (item.id === id ? applyIntakePatch(item, patch, now) : item));
}

export function removeIntakeItems(list: IntakeItem[], ids: string[]): IntakeItem[] {
  if (!ids.length) return list;
  const gone = new Set(ids);
  const next = list.filter((item) => !gone.has(item.id));
  return next.length === list.length ? list : next;
}

export function startReview(list: IntakeItem[], id: string, by: string, now = new Date().toISOString()): IntakeItem[] {
  const cur = list.find((item) => item.id === id);
  if (!cur || cur.status !== "pending") return list;
  return list.map((item) => (item.id === id ? { ...item, status: "reviewing", reviewedBy: by, reviewedAt: now, updatedAt: now } : item));
}

/** 반려·보류. 사유가 비어 있으면 목록을 그대로 돌려준다. */
export function closeIntake(list: IntakeItem[], id: string, status: "rejected" | "deferred", note: string, by: string, now = new Date().toISOString()): IntakeItem[] {
  const why = note.trim();
  const cur = list.find((item) => item.id === id);
  if (!why || !cur || !isOpenIntake(cur)) return list;
  return list.map((item) => (item.id === id ? { ...item, status, reviewNote: why, reviewedBy: by, reviewedAt: now, updatedAt: now } : item));
}

export function reopenIntake(list: IntakeItem[], id: string, now = new Date().toISOString()): IntakeItem[] {
  const cur = list.find((item) => item.id === id);
  if (!cur || (cur.status !== "rejected" && cur.status !== "deferred")) return list;
  return list.map((item) => (item.id === id ? { ...item, status: "pending", updatedAt: now } : item));
}

/**
 * 승인 → 새 실행 작업. 검수 기준은 품질 검수 항목이 되고,
 * 고객·고객 의견·법규 출처는 고객 인수 검수를 하나 더 붙인다.
 */
export function acceptIntakeAsNewTask(board: IntakeBoard, id: string, opts: AcceptOptions, now = new Date().toISOString()): (IntakeBoard & { taskId: string }) | null {
  const item = board.intakes.find((row) => row.id === id);
  if (!item || !isOpenIntake(item)) return null;
  const criteria = item.acceptanceCriteria;
  const linked = opts.linkedTaskIds ?? (item.targetScheduleTaskId ? [item.targetScheduleTaskId] : []);
  let task = makeExecutionTask(
    {
      title: item.title,
      ...(item.description ? { notes: item.description } : {}),
      priority: TASK_PRIORITY[item.priority],
      originIntakeId: item.id,
      linkedTaskIds: linked,
      ...(opts.bucketId ? { bucketId: opts.bucketId } : {}),
      order: board.executionTasks.reduce((max, row) => Math.max(max, row.order), -1) + 1,
    },
    now,
  );
  const primary: GateTagType = item.gateTagType ?? "qa";
  task = addGateTag(task, makeGateTag(primary, criteria), now);
  if (EXTERNAL_SOURCES.has(item.source) && primary !== "uat") task = addGateTag(task, makeGateTag("uat", criteria), now);
  task = logActivity(task, opts.by, `${intakeCode(item.seq)}에서 생성`, now);
  return {
    intakes: markAccepted(board.intakes, item.id, opts, now, { createdExecutionTaskId: task.id, ...(linked[0] ? { targetScheduleTaskId: linked[0] } : {}) }),
    executionTasks: [...board.executionTasks, task],
    taskId: task.id,
  };
}

/** 승인 → 기존 실행 작업에 `요구 반영 #n` 검수를 붙인다. 완료된 작업은 진행 중으로 돌아간다. */
export function mergeIntakeIntoTask(board: IntakeBoard, id: string, targetTaskId: string, opts: AcceptOptions, now = new Date().toISOString()): IntakeBoard | null {
  const item = board.intakes.find((row) => row.id === id);
  const target = board.executionTasks.find((row) => row.id === targetTaskId);
  if (!item || !isOpenIntake(item) || !target) return null;
  const gate = makeGateTag(item.gateTagType ?? "qa", item.acceptanceCriteria, `요구 반영 #${item.seq}`);
  let task = addGateTag(target, gate, now);
  if (!task.originIntakeId) task = { ...task, originIntakeId: item.id };
  task = logActivity(task, opts.by, `${intakeCode(item.seq)} 반영`, now);
  return {
    intakes: markAccepted(board.intakes, item.id, opts, now, { targetExecutionTaskId: targetTaskId }),
    executionTasks: board.executionTasks.map((row) => (row.id === targetTaskId ? task : row)),
  };
}

export function normalizeIntakes(raw: unknown, now = new Date().toISOString()): IntakeItem[] {
  if (!Array.isArray(raw)) return [];
  const seen = new Set<string>();
  const drafts: IntakeItem[] = [];
  for (const item of raw) {
    const next = normalizeOne(item, now);
    if (!next || seen.has(next.id)) continue;
    seen.add(next.id);
    drafts.push(next);
  }
  const used = new Set<number>();
  for (const item of drafts) {
    if (Number.isInteger(item.seq) && item.seq >= 1 && !used.has(item.seq)) used.add(item.seq);
    else item.seq = 0;
  }
  let max = used.size ? Math.max(...used) : 0;
  for (const item of drafts) {
    if (item.seq !== 0) continue;
    max += 1;
    item.seq = max;
    used.add(max);
  }
  return drafts;
}

function markAccepted(list: IntakeItem[], id: string, opts: AcceptOptions, now: string, link: Partial<Pick<IntakeItem, "createdExecutionTaskId" | "targetExecutionTaskId" | "targetScheduleTaskId">>): IntakeItem[] {
  return list.map((item) => {
    if (item.id !== id) return item;
    const next: IntakeItem = { ...item, ...link, status: "accepted", reviewedBy: opts.by, reviewedAt: now, updatedAt: now };
    const note = opts.note?.trim();
    if (note) next.reviewNote = note;
    if (typeof opts.estimatedEffortMM === "number" && opts.estimatedEffortMM >= 0) next.estimatedEffortMM = opts.estimatedEffortMM;
    if (typeof opts.scheduleImpactDays === "number" && opts.scheduleImpactDays >= 0) next.scheduleImpactDays = opts.scheduleImpactDays;
    return next;
  });
}

function applyIntakePatch(item: IntakeItem, patch: Partial<IntakeItem>, now: string): IntakeItem {
  const next: IntakeItem = { ...item, updatedAt: now };
  if (typeof patch.title === "string") next.title = patch.title.trim();
  if (typeof patch.description === "string") next.description = patch.description.trim();
  if (isSource(patch.source)) next.source = patch.source;
  if (isType(patch.type)) next.type = patch.type;
  if (isPriority(patch.priority)) next.priority = patch.priority;
  if (typeof patch.author === "string") next.author = patch.author.trim();
  if (typeof patch.organization === "string") {
    const org = patch.organization.trim();
    if (org) next.organization = org;
    else delete next.organization;
  }
  if (patch.acceptanceCriteria) next.acceptanceCriteria = cleanLines(patch.acceptanceCriteria);
  if (typeof patch.estimatedEffortMM === "number" && patch.estimatedEffortMM >= 0) next.estimatedEffortMM = patch.estimatedEffortMM;
  if (typeof patch.scheduleImpactDays === "number" && patch.scheduleImpactDays >= 0) next.scheduleImpactDays = patch.scheduleImpactDays;
  if (typeof patch.targetScheduleTaskId === "string") next.targetScheduleTaskId = patch.targetScheduleTaskId;
  if (typeof patch.reviewNote === "string") next.reviewNote = patch.reviewNote;
  if (isGateType(patch.gateTagType)) next.gateTagType = patch.gateTagType;
  return next;
}

function normalizeOne(raw: unknown, now: string): IntakeItem | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.id !== "string" || !r.id) return null;
  const item: IntakeItem = {
    id: r.id,
    seq: typeof r.seq === "number" ? r.seq : 0,
    title: typeof r.title === "string" ? r.title : "",
    description: typeof r.description === "string" ? r.description : "",
    source: isSource(r.source) ? r.source : "internal",
    type: isType(r.type) ? r.type : "newScope",
    status: isStatus(r.status) ? r.status : "pending",
    priority: isPriority(r.priority) ? r.priority : "medium",
    author: typeof r.author === "string" ? r.author : "",
    acceptanceCriteria: cleanLines(r.acceptanceCriteria),
    attachments: Array.isArray(r.attachments) ? (r.attachments as IntakeItem["attachments"]) : [],
    createdAt: typeof r.createdAt === "string" ? r.createdAt : now,
    updatedAt: typeof r.updatedAt === "string" ? r.updatedAt : now,
  };
  if (typeof r.organization === "string" && r.organization) item.organization = r.organization;
  if (typeof r.estimatedEffortMM === "number" && r.estimatedEffortMM >= 0) item.estimatedEffortMM = r.estimatedEffortMM;
  if (typeof r.scheduleImpactDays === "number" && r.scheduleImpactDays >= 0) item.scheduleImpactDays = r.scheduleImpactDays;
  if (typeof r.targetScheduleTaskId === "string" && r.targetScheduleTaskId) item.targetScheduleTaskId = r.targetScheduleTaskId;
  if (typeof r.targetExecutionTaskId === "string" && r.targetExecutionTaskId) item.targetExecutionTaskId = r.targetExecutionTaskId;
  if (typeof r.createdExecutionTaskId === "string" && r.createdExecutionTaskId) item.createdExecutionTaskId = r.createdExecutionTaskId;
  if (isGateType(r.gateTagType)) item.gateTagType = r.gateTagType;
  if (typeof r.reviewNote === "string" && r.reviewNote) item.reviewNote = r.reviewNote;
  if (typeof r.reviewedBy === "string" && r.reviewedBy) item.reviewedBy = r.reviewedBy;
  if (typeof r.reviewedAt === "string" && r.reviewedAt) item.reviewedAt = r.reviewedAt;
  return item;
}

function cleanLines(raw: unknown): string[] {
  if (!Array.isArray(raw)) return [];
  return raw.filter((line): line is string => typeof line === "string").map((line) => line.trim()).filter(Boolean);
}

function isSource(v: unknown): v is IntakeSource {
  return typeof v === "string" && (INTAKE_SOURCES as string[]).includes(v);
}
function isType(v: unknown): v is IntakeType {
  return typeof v === "string" && (INTAKE_TYPES as string[]).includes(v);
}
function isStatus(v: unknown): v is IntakeStatus {
  return typeof v === "string" && (INTAKE_STATUSES as string[]).includes(v);
}
function isPriority(v: unknown): v is IntakePriority {
  return typeof v === "string" && (INTAKE_PRIORITIES as string[]).includes(v);
}
function isGateType(v: unknown): v is GateTagType {
  return v === "qa" || v === "spec" || v === "uat" || v === "security" || v === "custom";
}
